'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { useParams } from 'next/navigation';
import SiteHeader from '@/components/SiteHeader';
import SiteFooter from '@/components/SiteFooter';
import RestrictedState from '@/components/shared/RestrictedState';
import { Skeleton } from '@/components/shared/Skeletons';
import { mockResources, getResourceBySlug, Resource } from '@/data/mockResources';
import { ArrowLeft, ArrowRight } from 'lucide-react';

const categoryColorMap: Record<string, string> = {
  'Getting Started': '#4CC9FF',
  'Trust & Safety': '#2DD4A0',
  'Community Channels': '#9B7FFF',
  'About This Platform': '#F0A832',
};

function ResourceSkeleton() {
  return (
    <div className="max-w-3xl mx-auto px-6 lg:px-10 py-10">
      <Skeleton className="h-4 w-28 mb-8" />
      <Skeleton className="h-3 w-24 rounded-full mb-3" />
      <Skeleton className="h-9 w-3/4 mb-6" />
      <div className="space-y-3">
        <Skeleton className="h-3 w-full" />
        <Skeleton className="h-3 w-full" />
        <Skeleton className="h-3 w-5/6" />
      </div>
    </div>
  );
}

function RelatedResourceCard({ resource }: { resource: Resource }) {
  const color = categoryColorMap[resource.category] || '#4CC9FF';
  return (
    <Link href={`/resources/${resource.slug}`} className="block group focus-visible:outline-none">
      <div className="neon-card p-4 group-focus-visible:border-[rgba(76,201,255,0.35)]">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-[16px]">{resource.icon}</span>
          <h4 className="font-semibold text-[13px] leading-snug" style={{ color: '#F2F4FF' }}>
            {resource.title}
          </h4>
        </div>
        <p className="text-[12px] line-clamp-1" style={{ color: '#A7A3C4' }}>{resource.excerpt}</p>
        <span className="flex items-center gap-1 text-[11px] font-medium mt-2" style={{ color }}>
          Read <ArrowRight size={10} />
        </span>
      </div>
    </Link>
  );
}

export default function ResourceDetailPage() {
  const params = useParams();
  const slug = params?.resourceSlug as string;
  const [isLoading, setIsLoading] = useState(true);
  const [resource, setResource] = useState<Resource | null>(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      const found = getResourceBySlug(slug);
      if (found) {
        setResource(found);
      } else {
        setNotFound(true);
      }
      setIsLoading(false);
    }, 500);
    return () => clearTimeout(timer);
  }, [slug]);

  const relatedResources = resource
    ? mockResources
        .filter((r) => r.slug !== slug && (resource.relatedSlugs?.includes(r.slug) || r.category === resource.category))
        .slice(0, 3)
    : [];

  const color = resource ? (categoryColorMap[resource.category] || '#4CC9FF') : '#4CC9FF';

  return (
    <div className="min-h-screen" style={{ background: '#07060C' }}>
      <SiteHeader theme="dark" />
      <main className="pt-16">
        {isLoading && <ResourceSkeleton />}

        {!isLoading && notFound && (
          <div className="max-w-3xl mx-auto px-6 lg:px-10 py-10">
            <Link href="/resources" className="inline-flex items-center gap-2 text-[13px] mb-8 transition-colors duration-150 hover:opacity-80" style={{ color: '#A7A3C4' }}>
              <ArrowLeft size={14} /> Back to Resources
            </Link>
            <RestrictedState
              variant="removed"
              title="Resource Unavailable"
              description="This resource has been removed or is no longer available. Browse the resources hub for other guides."
            />
          </div>
        )}

        {!isLoading && resource && (
          <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 py-10">
            <div className="max-w-3xl">
              {/* Back */}
              <Link href="/resources" className="inline-flex items-center gap-2 text-[13px] mb-8 transition-colors duration-150 hover:opacity-80" style={{ color: '#A7A3C4' }}>
                <ArrowLeft size={14} /> Back to Resources
              </Link>

              {/* Header */}
              <div className="flex items-start gap-4 mb-6">
                <div
                  className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0 text-[26px]"
                  style={{ background: `${color}14`, border: `1px solid ${color}28` }}
                >
                  {resource.icon}
                </div>
                <div>
                  <span
                    className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold uppercase tracking-wide mb-2"
                    style={{ background: `${color}14`, border: `1px solid ${color}28`, color }}
                  >
                    {resource.category}
                  </span>
                  <h1 className="font-display font-bold text-[26px] sm:text-[32px] leading-tight" style={{ color: '#F2F4FF' }}>
                    {resource.title}
                  </h1>
                </div>
              </div>

              <p className="text-[15px] leading-relaxed mb-8" style={{ color: '#A7A3C4' }}>
                {resource.excerpt}
              </p>

              {/* Body */}
              <div className="space-y-8 mb-10">
                {resource.body.map((section, idx) => (
                  <div key={idx}>
                    {section.heading && (
                      <h2 className="font-display font-semibold text-[18px] mb-3" style={{ color: '#F2F4FF' }}>
                        {section.heading}
                      </h2>
                    )}
                    {section.content && (
                      <p className="text-[15px] leading-relaxed mb-3" style={{ color: '#A7A3C4' }}>
                        {section.content}
                      </p>
                    )}
                    {section.list && section.list.length > 0 && (
                      <ul className="space-y-2">
                        {section.list.map((item, i) => (
                          <li key={i} className="flex items-start gap-3">
                            <div
                              className="w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0"
                              style={{ background: color }}
                            />
                            <span className="text-[14px] leading-relaxed" style={{ color: '#A7A3C4' }}>
                              {item}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                    {idx < resource.body.length - 1 && (
                      <div className="neon-divider mt-8" />
                    )}
                  </div>
                ))}
              </div>

              {/* Tags */}
              {resource.tags.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-10">
                  {resource.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-2.5 py-1 rounded-full text-[12px] font-medium"
                      style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)', color: '#A7A3C4' }}
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              )}

              {/* Related */}
              {relatedResources.length > 0 && (
                <div className="mb-10">
                  <h2 className="font-display font-semibold text-[18px] mb-4" style={{ color: '#F2F4FF' }}>
                    Related Resources
                  </h2>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {relatedResources.map((r) => <RelatedResourceCard key={r.id} resource={r} />)}
                  </div>
                </div>
              )}

              {/* Back CTA */}
              <div className="pt-8" style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}>
                <Link href="/resources" className="btn-secondary text-[14px] px-5 py-2.5 inline-flex items-center gap-2">
                  <ArrowLeft size={14} /> Back to Resources
                </Link>
              </div>
            </div>
          </div>
        )}
      </main>
      <SiteFooter />
    </div>
  );
}
